export async function searchCEP(cep) {
    try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();

        if (data.erro) {
            console.error("CEP not found");
            return null;
        }

        const result = {
            street: data.logradouro,
            neighborhood: data.bairro,
            city: data.localidade,
            state: data.uf,
        };

        return result;
    } catch (error) {
        console.error("Error in zip code search:", error.message);
        return null;
    }
}

// Geocodifica um endereço em coordenadas via Nominatim (OpenStreetMap), gratuito e sem chave.
async function geocodeAddress(query) {
    try {
        if (!query) {
            return null;
        }
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=br&q=${encodeURIComponent(query)}`;
        const response = await fetch(url, {
            headers: { 'Accept-Language': 'pt-BR' },
        });
        if (!response.ok) {
            return null;
        }
        const results = await response.json();
        if (!Array.isArray(results) || results.length === 0) {
            return null;
        }
        const { lat, lon } = results[0];
        if (lat == null || lon == null) {
            return null;
        }
        return { lat: parseFloat(lat), lon: parseFloat(lon) };
    } catch (error) {
        console.error("Error geocoding address:", error.message);
        return null;
    }
}

// Cache em memória de coordenadas por CEP (evita rebuscas e respeita o limite do Nominatim).
const coordinatesCache = new Map();

// Busca as coordenadas (latitude/longitude) de um CEP.
// 1) Tenta a BrasilAPI (que às vezes já retorna coordenadas).
// 2) Se a BrasilAPI não trouxer coordenadas (ex.: serviço open-cep), geocodifica
//    o endereço/cidade resolvido via Nominatim (OpenStreetMap).
export async function getCoordinatesByCep(cep) {
    const cepDigits = (cep || '').replace(/\D/g, '');
    if (cepDigits.length !== 8) {
        return null;
    }

    if (coordinatesCache.has(cepDigits)) {
        return coordinatesCache.get(cepDigits);
    }

    const coordinates = await resolveCoordinatesByCep(cepDigits);
    coordinatesCache.set(cepDigits, coordinates);
    return coordinates;
}

async function resolveCoordinatesByCep(cepDigits) {
    try {
        let street;
        let neighborhood;
        let city;
        let state;

        try {
            const response = await fetch(`https://brasilapi.com.br/api/cep/v2/${cepDigits}`);
            if (response.ok) {
                const data = await response.json();
                const coordinates = data && data.location && data.location.coordinates;
                if (coordinates && coordinates.latitude != null && coordinates.longitude != null) {
                    return {
                        lat: parseFloat(coordinates.latitude),
                        lon: parseFloat(coordinates.longitude),
                    };
                }
                street = data.street;
                neighborhood = data.neighborhood;
                city = data.city;
                state = data.state;
            }
        } catch (error) {
            console.error("Error fetching coordinates from BrasilAPI:", error.message);
        }

        // Fallback: completa os dados de endereço via ViaCEP, se necessário.
        if (!city) {
            const viaCep = await searchCEP(cepDigits);
            if (viaCep) {
                street = street || viaCep.street;
                neighborhood = neighborhood || viaCep.neighborhood;
                city = viaCep.city;
                state = viaCep.state;
            }
        }

        if (!city || !state) {
            return null;
        }

        // Tenta geocodificar do endereço mais específico para o mais genérico.
        const candidates = [];
        if (street) {
            candidates.push(`${street}, ${city}, ${state}, Brasil`);
        }
        if (neighborhood) {
            candidates.push(`${neighborhood}, ${city}, ${state}, Brasil`);
        }
        candidates.push(`${city}, ${state}, Brasil`);

        for (const candidate of candidates) {
            const coordinates = await geocodeAddress(candidate);
            if (coordinates) {
                return coordinates;
            }
        }

        return null;
    } catch (error) {
        console.error("Error fetching coordinates by CEP:", error.message);
        return null;
    }
}

// Fórmula de Haversine: distância em km entre dois pontos geográficos.
export function haversineDistanceKm(lat1, lon1, lat2, lon2) {
    const R = 6371; // raio da Terra em km
    const toRad = (degrees) => (degrees * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Distância aproximada em km entre dois CEPs (null se algum CEP não tiver coordenadas).
export async function distanceBetweenCeps(originCep, destinationCep) {
    const origin = await getCoordinatesByCep(originCep);
    const destination = await getCoordinatesByCep(destinationCep);
    if (!origin || !destination) {
        return null;
    }
    return haversineDistanceKm(origin.lat, origin.lon, destination.lat, destination.lon);
}

